/**
 * Pure rules for the shared MCP Authentication Setup - one copy for every host
 * (manual registration, Agent Import's MCP Connectivity drawer, MCP Services).
 *
 * The form keeps values of unmounted branches so switching back loses nothing;
 * these helpers decide what the selected branch actually uses, so a hidden
 * branch is never validated, sent, or fingerprinted.
 */
import type {
  AccessPurpose,
  ConfiguredSecrets,
  McpAuthFormValues,
} from "@/api/temp/McpAuthenticationService";

type AuthConfig = Record<string, unknown>;

/**
 * A string value, or a dropdown option's id/name - the same normalization as
 * manual registration's `normalizeStringValue`, kept local so the mock layer
 * can import these rules without pulling in the wizard.
 */
const text = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const option = value as { id?: unknown; name?: unknown };
  if (typeof option.id === "string") return option.id.trim();
  if (typeof option.name === "string") return option.name.trim();
  return "";
};

const authConfigOf = (values: McpAuthFormValues): AuthConfig =>
  (values.authConfig as AuthConfig | undefined) ?? {};

const path = (key: string) => `authConfig.${key}`;

const OAUTH_KEYS = [
  "grantType",
  "serviceIdentity",
  "exchangeType",
  "agentApplicationId",
  "clientId",
  "clientSecret",
  "scope",
  "tokenEndpoint",
  "authorizationEndpoint",
] as const;

const JWT_KEYS = ["account", "username", "privateKey", "passphrase"] as const;

/**
 * Client registration keys. Only hosts that offer the choice render them, so
 * they are stripped only when present - manual registration never sees them.
 */
export const REGISTRATION_KEYS = [
  "clientRegistrationMethod",
  "clientMetadataSource",
  "clientMetadataProfile",
  "clientMetadataUrl",
] as const;

const CIMD_DETAIL_KEYS = [
  "clientMetadataSource",
  "clientMetadataProfile",
  "clientMetadataUrl",
] as const;

const VALIDATION_PATHS = ["oauthValidationCode", "oauthValidatedInputs"];

/** Credentials that are stored write-only and reported as configured. */
export const SECRET_KEYS = [
  "clientSecret",
  "privateKey",
  "passphrase",
] as const;

export const isCimdRegistration = (authConfig: AuthConfig): boolean =>
  text(authConfig.clientRegistrationMethod) === "CIMD";

/**
 * Field paths the selected branch does not use. Clearing them before leaving
 * the step (or saving) keeps stale values from another selection out of the
 * payload. The OAuth / JWT / None lists match manual registration's original
 * rules exactly.
 */
export function inactiveAuthFieldPaths(values: McpAuthFormValues): string[] {
  const authType = text(values.authType);
  const authConfig = authConfigOf(values);
  const present = (keys: readonly string[]) =>
    keys.filter(key => key in authConfig).map(path);

  if (authType === "key_pair") {
    return [
      ...OAUTH_KEYS.map(path),
      ...present(REGISTRATION_KEYS),
      ...VALIDATION_PATHS,
    ];
  }

  if (authType === "oauth" || authType === "passthrough") {
    const serviceIdentity = text(authConfig.serviceIdentity);
    const grantType = text(authConfig.grantType);
    const paths: string[] = JWT_KEYS.map(path);

    if (serviceIdentity !== "AGENT") paths.push(path("agentApplicationId"));
    if (serviceIdentity !== "CUSTOM") {
      paths.push(path("clientId"), path("clientSecret"));
      // A gateway identity carries its own registration.
      paths.push(...present(REGISTRATION_KEYS));
    } else if (isCimdRegistration(authConfig)) {
      // The metadata URL is the client ID; a shared secret has no place in
      // a public client document.
      paths.push(path("clientId"), path("clientSecret"));
    } else {
      paths.push(...present(CIMD_DETAIL_KEYS));
    }

    if (authType === "passthrough") {
      paths.push(path("grantType"), path("exchangeType"));
    } else if (grantType !== "TOKEN_EXCHANGE") {
      paths.push(path("exchangeType"));
    }
    return paths;
  }

  return [
    ...OAUTH_KEYS.map(path),
    ...JWT_KEYS.map(path),
    ...present(REGISTRATION_KEYS),
    ...VALIDATION_PATHS,
  ];
}

/** Removes a `head` or `head.key` path from a shallow copy. */
function withoutPath(
  values: McpAuthFormValues,
  fieldPath: string,
): McpAuthFormValues {
  const [head, key] = fieldPath.split(".");
  if (!key) {
    const next = { ...values };
    delete next[head];
    return next;
  }
  const nested = { ...((values[head] as AuthConfig | undefined) ?? {}) };
  delete nested[key];
  return { ...values, [head]: nested };
}

/** The values with every inactive branch removed - what a save may send. */
export function activeAuthValues(values: McpAuthFormValues): McpAuthFormValues {
  return inactiveAuthFieldPaths(values).reduce(withoutPath, values);
}

export interface AuthValidationOptions {
  /** Edit of a saved profile: stored secrets need not be re-entered. */
  isEdit?: boolean;
}

/**
 * The cross-field gate for the Authentication Setup: `true` when the selected
 * branch is complete. Field-level validators (required, URL) still run on
 * their own fields; this covers the combinations they cannot express.
 */
export function isAuthenticationComplete(
  values: McpAuthFormValues,
  { isEdit = false }: AuthValidationOptions = {},
): boolean {
  const authType = text(values.authType);
  if (!authType) return false;
  if (authType !== "oauth" && authType !== "passthrough") return true;

  const authConfig = authConfigOf(values);
  const serviceIdentity = text(authConfig.serviceIdentity);
  if (!serviceIdentity) return false;
  // Grant type / exchange type / agent-application checks only apply to the
  // full OAuth flow - passthrough never renders those controls.
  if (authType === "oauth" && !text(authConfig.grantType)) return false;
  if (
    authType === "oauth" &&
    serviceIdentity === "AGENT" &&
    !text(authConfig.agentApplicationId)
  ) {
    return false;
  }
  if (serviceIdentity === "CUSTOM") {
    if (isCimdRegistration(authConfig)) {
      if (!text(authConfig.clientMetadataUrl)) return false;
    } else if (
      (!text(authConfig.clientId) || !text(authConfig.clientSecret)) &&
      !isEdit
    ) {
      return false;
    }
  }
  if (
    authType === "oauth" &&
    text(authConfig.grantType) === "TOKEN_EXCHANGE" &&
    !text(authConfig.exchangeType)
  ) {
    return false;
  }
  return true;
}

/**
 * Splits values for a write: secrets leave only as "replaced" markers the
 * secure write path fills, so no secret or key reaches a summary, a log or a
 * persisted mock. An empty secret on edit means "keep the stored one".
 */
export function redactAuthValues(values: McpAuthFormValues): {
  formValues: McpAuthFormValues;
  secretsReplaced: Array<keyof ConfiguredSecrets>;
} {
  const authConfig = { ...authConfigOf(values) };
  const secretsReplaced: Array<keyof ConfiguredSecrets> = [];
  for (const key of SECRET_KEYS) {
    const value = authConfig[key];
    if (value instanceof File || text(value)) secretsReplaced.push(key);
    delete authConfig[key];
  }
  const formValues: McpAuthFormValues = { ...values, authConfig };
  for (const key of VALIDATION_PATHS) delete formValues[key];
  delete formValues.oauthTokenName;
  return { formValues, secretsReplaced };
}

/** What a saved configuration's OAuth client points at, for usage and impact. */
export type ClientProfileReference =
  | { kind: "PROFILE"; profileId: string; purposes: AccessPurpose[] }
  | { kind: "GATEWAY"; purposes: AccessPurpose[] };

/**
 * The OAuth client profile a configuration references, if any. OAuth uses its
 * client for discovery and runtime; pass-through only for discovery, since at
 * runtime it forwards the caller's token. A direct metadata URL is a
 * service-local reference, never a profile.
 */
export function clientProfileReferenceOf(
  values: McpAuthFormValues,
): ClientProfileReference | undefined {
  const active = activeAuthValues(values);
  const authType = text(active.authType);
  if (authType !== "oauth" && authType !== "passthrough") return undefined;
  const authConfig = authConfigOf(active);
  const purposes: AccessPurpose[] =
    authType === "oauth"
      ? ["METADATA_DISCOVERY", "RUNTIME"]
      : ["METADATA_DISCOVERY"];
  const serviceIdentity = text(authConfig.serviceIdentity);
  if (serviceIdentity === "GATEWAY") return { kind: "GATEWAY", purposes };
  if (
    serviceIdentity !== "CUSTOM" ||
    !isCimdRegistration(authConfig) ||
    text(authConfig.clientMetadataSource) !== "MANAGED_PROFILE"
  ) {
    return undefined;
  }
  const profileId = text(authConfig.clientMetadataProfile);
  return profileId ? { kind: "PROFILE", profileId, purposes } : undefined;
}

/**
 * Which inputs a check for `purpose` depends on. Evidence recorded against a
 * different fingerprint is stale, so editing an input invalidates exactly the
 * results that used it:
 *  - discovery identity, registration, endpoints and scopes -> metadata access;
 *  - grant and exchange type -> runtime only (discovery reuses the client, not
 *    the grant);
 *  - a credential rotation -> both, via `credentialVersion`;
 *  - a display name -> neither.
 */
export function authFingerprint(
  values: McpAuthFormValues,
  purpose: AccessPurpose,
  credentialVersion = 0,
): string {
  const active = activeAuthValues(values);
  const authType = text(active.authType);
  const authConfig = authConfigOf(active);
  const pick = (keys: string[]) =>
    keys.map(key => `${key}=${text(authConfig[key])}`);

  const client = [
    "serviceIdentity",
    "clientRegistrationMethod",
    "clientMetadataUrl",
    "clientId",
    "scope",
    "tokenEndpoint",
    "authorizationEndpoint",
  ];

  let parts: string[];
  if (authType === "oauth") {
    parts = pick(
      purpose === "RUNTIME" ? [...client, "grantType", "exchangeType"] : client,
    );
  } else if (authType === "passthrough") {
    // Passthrough's OAuth configuration is the discovery client; runtime
    // forwards the caller's token and depends on none of it.
    parts = purpose === "METADATA_DISCOVERY" ? pick(client) : [];
  } else if (authType === "key_pair") {
    parts = pick(["account", "username"]);
  } else {
    parts = [];
  }
  return [
    purpose,
    authType || "unset",
    `cred=${credentialVersion}`,
    ...parts,
  ].join("|");
}
